import {
  AggressorFlagEnum,
  getPriceDiff,
  getSpreadPctDisplay,
  SideEnum,
  SpreadUnit,
  type MarketDataSnapshot,
} from '@talos/kyoko';
import Big, { type BigSource } from 'big.js';
import { get } from 'lodash-es';

// This seemingly arbitrary number gives us a 2px space
// between the size bucket border and the spread bar.
const MAX_BAR_WIDTH = 0.68;

export function getLadderBackgroundSize(size: string | number = 0, largest: string | number = 0): number {
  const parsedSize = Big(size);
  const parsedLargest = Big(largest);

  if (parsedLargest.lte(parsedSize)) {
    return MAX_BAR_WIDTH;
  }

  if (parsedSize.eq(0) || parsedLargest.eq(0)) {
    return 0;
  }
  return parsedSize.div(parsedLargest).mul(MAX_BAR_WIDTH).round(2).toNumber();
}

export function getSizeSpread(rows: MarketDataSnapshot): number[] {
  const bids = rows?.Bids || [];
  const spreads = bids.map((row, index) => {
    if (!rows.Offers[index]) {
      return 0;
    }
    const bidPrice = get(rows, `Bids[${index}].Price`);
    const offerPrice = get(rows, `Offers[${index}].Price`);

    return getPriceSpread({ bidPrice, offerPrice });
  });

  // find the largest
  const largest = Math.max.apply(0, spreads);

  // calculate relative %s
  return spreads.map(spread => {
    return getLadderBackgroundSize(spread, largest);
  });
}

export function getPriceSpread({
  bidPrice,
  offerPrice,
}: {
  bidPrice: BigSource | undefined;
  offerPrice: BigSource | undefined;
}): number {
  if (bidPrice == null || offerPrice == null) {
    return 0;
  }

  if (Big(offerPrice || '').gt(bidPrice)) {
    return Big(offerPrice).sub(bidPrice).toNumber();
  }

  return 0;
}

export function getPriceGivenIntent(side: SideEnum, intent: AggressorFlagEnum, bid: string, offer: string): string {
  if (side === SideEnum.Buy) {
    return intent === AggressorFlagEnum.Passive ? bid : offer;
  }
  return intent === AggressorFlagEnum.Passive ? offer : bid;
}

/**
 * Get the spread display value between bid and offer prices.
 * - When prices are in cash terms and we want BPS spread: calculate spread as percentage (BPS)
 * - When prices are already in BPS (isPricingModeBPS): use absolute difference
 * - When differenceUnit is PRICE: use absolute cash difference
 */
export function getSpreadDisplayValue(
  bidPrice: string,
  offerPrice: string,
  differenceUnit: SpreadUnit,
  isPricingModeBPS: boolean
): string {
  const shouldShowPercentageSpread = differenceUnit === SpreadUnit.BPS && !isPricingModeBPS;
  return shouldShowPercentageSpread ? getSpreadPctDisplay(bidPrice, offerPrice) : getPriceDiff(bidPrice, offerPrice);
}
